import React, { useState, useEffect } from 'react';
import { Button, Container } from '@bsf/force-ui';
import toast, { Toaster } from 'react-hot-toast';
import { __ } from "@wordpress/i18n";
import { ChevronLeft, ArrowRight } from "lucide-react";

const SocialProfiles = ({ setCurrentStep }) => {
  const [socialProfiles, setSocialProfiles] = useState({
    facebook: '',
    twitter: '',
    pinterest: '',
    instagram: '',
    youtube: '',
    linkedin: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const getSocialProfiles = () => {
      fetch(schemaSettingsData.ajax_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'get_social_profiles',
          _ajax_nonce: schemaSettingsData.ajax_nonce,
        }),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.success) {
            const profiles = result.data;
            setSocialProfiles(profiles);
          } else {
            console.error('Error:', result.data);
          }
        })
        .catch((error) => console.error('AJAX error:', error));
    };

    getSocialProfiles();
  }, []);

  const handleChange = (name, value) => {
    setSocialProfiles(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateSocialProfiles = () => {
    const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[a-z]{2,}(\/\S*)?$/i;
    const newErrors = {};
    const toastErrors = [];
    Object.entries(socialProfiles).forEach(([key, value]) => {
      if (key !== "other") {
        if (value !== "" && !urlPattern.test(value)) {
          newErrors[key] = __('Entered URL is Invalid', 'wp-schema-pro');
        }
     }else{
        if (Array.isArray(value)) {
          value.forEach((val, index) => {
            if (val && !urlPattern.test(val)) {
              newErrors[`${key}[${index}]`] = __('Entered URL is Invalid', 'wp-schema-pro');
            }
          });
      }
    }
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(__('Please enter valid URL\n', 'wp-schema-pro') );
      return;
    }

    saveSocialProfiles();
  };

  const saveSocialProfiles = () => {
    fetch(schemaSettingsData.ajax_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        action: 'save_social_profiles',
        profiles: JSON.stringify(socialProfiles),
        _ajax_nonce: schemaSettingsData.ajax_nonce,
      }),
    })
      .then(response => response.json())
      .then(result => {
        if (result.success) {
          setCurrentStep(4);
        } else {
          console.error(result.data);
        }
      })
      .catch(error => console.error('AJAX error:', error));
  };

  const renderInput = (label, name, placeholder) => (
    <Container
      align="stretch"
      className="flex flex-column bg-background-primary"
      containerType="flex"
      direction="column"
      style={{
        padding: "24px",
        borderRadius: "8px",
      }}
    >
      <div className="flex flex-column items-center mt-1 w-full gap-3" style={{ paddingTop: '16px' }}>
        <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
          <div className='text-base text-field-label font-semibold m-0 mb-2'>{__(label, 'wp-schema-pro')}</div>
          <input
            name={name}
            className='w-full border px-2'
            type="text"
            value={socialProfiles[name]}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            placeholder={__(placeholder, 'wp-schema-pro')}
            style={{
              height: '48px',
              borderColor: '#e0e0e0',
              outline: 'none',
              boxShadow: 'none',
              color: '#363b46',
            }}
            onFocus={(e) => e.target.style.borderColor = '#E62A5E'}
            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
          />
          {errors[name] && (
            <span className="text-sm text-red-600 pt-1" style={{ color: 'red'}}>{errors[name]}</span>
          )}
        </Container.Item>
      </div>
    </Container>
  );

  return (
    <div className="bg-background-primary border-[0.5px] items-start justify-center border-subtle rounded-xl shadow-sm mb-6 pl-10 pr-10 pb-10 flex flex-col" style={{ maxWidth: '45%', paddingTop: '1rem' }}>
      <div className="px-1">
        <div className="flex flex-col">
          <h1 className="text-[#1E293B] mb-2 text-2xl schema-65-width" style={{ lineHeight: '1.3em' }}>
            <span>{__("Social Profiles", "wp-schema-pro")}</span>
          </h1>
          <span className="text-sm font-medium mb-6 schema-88-width" style={{ fontSize: '14px', lineHeight: '1.5em', color: '#64748B' }}>
            {__("Please enter all your possible social media profiles. These links can appear in the knowledge panel of the search results for your website.", "wp-schema-pro")}
          </span>
        </div>

        {renderInput('Facebook Page URL', 'facebook', 'https://facebook.com/your-page-url')}
        {renderInput('X (formerly Twitter) URL', 'twitter', 'https://twitter.com/your-page-url')}
        {renderInput('Pinterest URL', 'pinterest', 'https://pinterest.com/your-page-url')}
        {renderInput('Instagram URL', 'instagram', 'https://instagram.com/your-page-url')}
        {renderInput('Youtube URL', 'youtube', 'https://youtube.com/your-channel-url')}
        {renderInput('LinkedIn URL', 'linkedin', 'http://linkedin.com/company/your-company-url/')}

        <Toaster
          position="top-right"
          reverseOrder={false}
          gutter={8}
          containerStyle={{
            top: 20,
            right: 20,
            marginTop: '40px',
          }}
          toastOptions={{
            duration: 1000,
            style: {
              background: 'white',
            },
            success: {
              duration: 2000,
              iconTheme: {
                primary: '#6005ff',
                secondary: '#fff',
              },
            },
          }}
        />

        <div className="flex justify-between items-center pt-1 gap-4 schema-onboarding-bottom" style={{ paddingTop: '32px' }}>
          <Button
            className="flex items-center gap-1 schema-remove-ring"
            icon={<ChevronLeft />}
            variant="outline"
            onClick={() => setCurrentStep(2)}
          >
            {__("Back", "wp-schema-pro")}
          </Button>
          <div className="flex justify-end items-center gap-3">
            <Button
              className="schema-remove-ring"
              variant="ghost"
              onClick={() => setCurrentStep(4)}
            >
              {__("Skip", "wp-schema-pro")}
            </Button>
            <Button
              className="flex items-center gap-1 schema-remove-ring"
              icon={<ArrowRight />}
              iconPosition="right"
              style={{
                backgroundColor: "#E62A5E",
                transition: "background-color 0.3s ease",
                padding: "12px",
                outlineColor: "#E62A5E",
              }}
              onClick={validateSocialProfiles}
            >
              {__(" Continue Setup", "wp-schema-pro")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialProfiles;
